/**
 * Scorecard display helpers for set-win circles and tiebreak superscripts.
 *
 * Tiebreak rule (first to 7 by 2) → only ONE number is shown:
 * - Loser TB points ≤ 5 → put that number above the 6
 * - Winner TB points ≥ 8 → put that number above the 7
 *   (loser had winner − 2)
 *
 * Entry / storage rules (same math):
 * - Winner has exactly 7 → loser may be 0..5 (0 allowed if only winner entered 7)
 * - Winner has 8+ → loser is always winner − 2 (auto-fill when loser blank/0)
 */

export function didWinSet(games, oppGames) {
  return Number(games || 0) > Number(oppGames || 0);
}

/**
 * Normalize + validate TB points for a 6-6 / 7-6 set.
 * Returns filled supers for both players when ok.
 *
 * @returns {{
 *   ok: boolean,
 *   p1Sup?: number,
 *   p2Sup?: number,
 *   p1Won?: boolean,
 *   error?: string
 * }}
 */
export function normalizeTiebreakScores(p1Games, p2Games, p1SupRaw, p2SupRaw) {
  const g1 = Number(p1Games) || 0;
  const g2 = Number(p2Games) || 0;
  let s1 = Number(p1SupRaw) || 0;
  let s2 = Number(p2SupRaw) || 0;

  const isTbSet =
    (g1 === 7 && g2 === 6) ||
    (g1 === 6 && g2 === 7) ||
    (g1 === 6 && g2 === 6);

  if (!isTbSet) {
    return { ok: false, error: "Not a tiebreak set." };
  }

  if (s1 <= 0 && s2 <= 0) {
    return {
      ok: false,
      error: "Please enter tiebreak scores.",
    };
  }

  // Who won the set: prefer games (7-6), else higher TB on 6-6
  let p1Won;
  if (g1 === 7 && g2 === 6) p1Won = true;
  else if (g1 === 6 && g2 === 7) p1Won = false;
  else if (s1 > 0 && s2 <= 0) p1Won = true;
  else if (s2 > 0 && s1 <= 0) p1Won = false;
  else p1Won = s1 > s2;

  let winnerTb = p1Won ? s1 : s2;
  let loserTb = p1Won ? s2 : s1;

  // Deduce missing side from the single entered number
  if (winnerTb > 0 && loserTb <= 0) {
    if (winnerTb < 7) {
      return {
        ok: false,
        error: "Tiebreak winner must have at least 7 points.",
      };
    }
    if (winnerTb === 7) {
      // Explicit note: loser may be 0 when winner posted 7
      loserTb = 0;
    } else {
      // Winner 8+ ⇒ loser always winner − 2
      loserTb = winnerTb - 2;
    }
  } else if (loserTb > 0 && winnerTb <= 0) {
    if (loserTb <= 5) {
      winnerTb = 7;
    } else {
      winnerTb = loserTb + 2;
    }
  }

  // Validate final pair
  if (winnerTb < 7) {
    return {
      ok: false,
      error: "Tiebreak winner must have at least 7 points.",
    };
  }

  if (winnerTb === 7) {
    if (loserTb < 0 || loserTb > 5) {
      return {
        ok: false,
        error:
          "When the tiebreak winner has 7, the loser must have between 0 and 5 points.",
      };
    }
  } else if (loserTb !== winnerTb - 2) {
    return {
      ok: false,
      error: `When the tiebreak winner has ${winnerTb}, the loser must have ${winnerTb - 2} (win by 2).`,
    };
  }

  if (winnerTb - loserTb < 2) {
    return {
      ok: false,
      error: "Tiebreak winner must have at least 2 points more than the opponent.",
    };
  }

  return {
    ok: true,
    p1Won,
    p1Sup: p1Won ? winnerTb : loserTb,
    p2Sup: p1Won ? loserTb : winnerTb,
  };
}

/**
 * @returns {{ topSup: number|null, bottomSup: number|null }}
 */
export function resolveTiebreakSuperscript(topGames, bottomGames, topSupRaw, bottomSupRaw) {
  const topGamesN = Number(topGames) || 0;
  const bottomGamesN = Number(bottomGames) || 0;
  let topSup = Number(topSupRaw) || 0;
  let bottomSup = Number(bottomSupRaw) || 0;

  const isTbSet =
    (topGamesN === 7 && bottomGamesN === 6) ||
    (topGamesN === 6 && bottomGamesN === 7) ||
    (topGamesN === 6 && bottomGamesN === 6 && (topSup > 0 || bottomSup > 0));

  if (!isTbSet) {
    return { topSup: null, bottomSup: null };
  }

  if (topSup <= 0 && bottomSup <= 0) {
    return { topSup: null, bottomSup: null };
  }

  const normalized = normalizeTiebreakScores(
    topGamesN,
    bottomGamesN,
    topSup,
    bottomSup
  );
  if (normalized.ok) {
    topSup = normalized.p1Sup;
    bottomSup = normalized.p2Sup;
  }

  const topWonSet =
    topGamesN > bottomGamesN ||
    (topGamesN === bottomGamesN && topSup > bottomSup);
  const winnerTb = topWonSet ? topSup : bottomSup;
  const loserTb = topWonSet ? bottomSup : topSup;

  let showWinner = null;
  let showLoser = null;

  if (loserTb <= 5) {
    // Includes 0–5: show above the 6
    showLoser = loserTb;
  } else if (winnerTb >= 8) {
    // Extended TB: show above the 7
    showWinner = winnerTb;
  }

  return {
    topSup: topWonSet ? showWinner : showLoser,
    bottomSup: topWonSet ? showLoser : showWinner,
  };
}
